import { useCallback } from 'react';

/**
 * useEditableCellTabNavigation
 *
 * Tab, while editing a DataGrid row, would otherwise move the browser's native focus in raw DOM
 * order. This intercepts Tab so it instead moves along the row's `editable` columns via the grid's
 * own cell-focus API, keeping keyboard navigation correct regardless of column layout/order.
 *
 * @param {import('@mui/x-data-grid-pro').GridApiCommunity} apiRef - the grid's api ref
 * @param {import('@mui/x-data-grid-pro').GridRowId} id - the row id of the cell being edited
 * @param {string} field - the column field of the cell being edited
 * @returns {(e: React.KeyboardEvent) => void} - keydown handler to attach to the cell's editable input
 */
export function useEditableCellTabNavigation(apiRef, id, field) {
  return useCallback(
    e => {
      if (e.key === 'Tab') {
        const editableColumns = apiRef.current.getAllColumns().filter(col => col.editable);
        const currentIndex = editableColumns.findIndex(col => col.field === field);
        const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;
        if (nextIndex >= 0 && nextIndex < editableColumns.length) {
          e.preventDefault();
          apiRef.current.setCellFocus(id, editableColumns[nextIndex].field);
        }
      }
    },
    [apiRef, id, field]
  );
}
